import React from "react";
import { QRCodeSVG } from "qrcode.react";
import { useParams } from "react-router";
import { useSelector } from "react-redux";
import Modal from "react-bootstrap/Modal";
import { CheckIcon, CloseIcon, ContentCopyIcon } from "../components/icons";
import { STATUS_DETAILS } from "../ducks/consts";
import { selectLayout } from "../ducks/shopSelectors";
import CheckoutSection from "../components/CheckoutSection";
import watchOrderStatus, { atrasoInicial } from "../utils/watchOrderStatus";
import {
  andamentoDoPedido,
  codigoFinalDoPedido,
  etapasDoPedido,
  pedidoEncerrado,
} from "../utils/orderProgress";
import CommonHeader from "../containers/CommonHeader";
import mpURL from "../assets/payment-flat-rounded/mercado-pago.svg";
import SelfCheckoutQRCodeStatus from "./Selfcheckout/SelfCheckoutQRCodeStatus";
import SelfCheckoutTotemStatus from "./Selfcheckout/SelfCheckoutTotemStatus";
import OrderDetailsStatus from "./OrderDetailsStatus";
import InstallAppCard from "./InstallAppCard";
import SelfCheckoutTableStatus from "./Selfcheckout/SelfCheckoutTableStatus";

export default function OrderFollowupScreen() {
  const params = useParams();
  const [isModalVisible, setModalVisible] = React.useState(true);
  const [status, setStatus] = React.useState([]);

  const showOnlyStatus = useSelector((state) => state.appState.showOnlyStatus);
  const shop = useSelector((state) => state.shop);
  const { sharedDevice } = useSelector(selectLayout);
  const shortReference = parseInt(params.shortReference, 10);
  const lastCache = useSelector((state) => state.checkout.lastCheckoutCache);

  const cache =
    lastCache && shortReference === lastCache.shortReference ? lastCache : null;
  const order = cache
    ? {
        id: cache.orderId,
        shopId: cache.shopId,
        createdAt: cache.createdAt,
        deliveryDateTime: cache.deliveryDateTime,
        toGo: cache.checkout.toGo,
        toDelivery: cache.checkout.toDelivery,
        isInLoco: cache.isInLoco,
        tableId: cache.tableId,
        isTakingHome: cache.checkout.isTakingHome,
      }
    : {
        id: null,
        shopId: null,
        createdAt: null,
        toGo: null,
        toDelivery: null,
      };

  const {
    isSelfcheckout,
    isSelfOrderTable,
    isSelfcheckoutQRCode,
    isSelfcheckoutTotem,
  } = shop;

  const pollingDelay = isSelfcheckoutTotem
    ? 2000
    : isSelfcheckoutQRCode
      ? 60000
      : 10000;

  const codigoFinal = codigoFinalDoPedido({
    isSelfcheckout,
    toDelivery: order.toDelivery,
  });

  React.useEffect(() => {
    if (!order.id) return;
    if (isSelfOrderTable) return;

    return watchOrderStatus(order.id, {
      delayMs: pollingDelay,
      // Logo depois do pedido, a primeira consulta espera três ciclos (30s;
      // 6s no totem; 3 min no QR Code). Reaberta depois disso, consulta na hora.
      inicioMs: atrasoInicial(order.createdAt, pollingDelay * 3),
      // no celular do cliente, para com a aba oculta; POS e totem seguem
      pausarOculta: !sharedDevice,
      isFinished: (data) => pedidoEncerrado(data, codigoFinal),
      onStatus: setStatus,
    });
  }, [
    order.id,
    order.createdAt,
    codigoFinal,
    isSelfOrderTable,
    pollingDelay,
    sharedDevice,
  ]);

  if (isSelfOrderTable) {
    return (
      <SelfCheckoutTableStatus shortReference={shortReference} order={order} />
    );
  }

  if (isSelfcheckoutQRCode) {
    return (
      <SelfCheckoutQRCodeStatus
        shortReference={shortReference}
        shop={shop}
        cache={cache}
        order={order}
        status={status}
      />
    );
  }

  if (isSelfcheckoutTotem) {
    return (
      <SelfCheckoutTotemStatus
        shortReference={shortReference}
        cache={cache}
        order={order}
        status={status}
      />
    );
  }

  if (!order.id) {
    return (
      <>
        <CommonHeader text="Acompanhe seu pedido" showBack={!showOnlyStatus} />
        <div className="empty-state">
          <p className="empty-state__title">{`Pedido nº ${shortReference} não encontrado`}</p>
          <p>Ele pode ter sido feito em outro aparelho.</p>
        </div>
      </>
    );
  }

  const paymentQRData = cache ? cache.paymentQRData : null;
  const paymentPreference = cache ? cache.paymentPreference : null;
  const showPaymentStep = paymentQRData || paymentPreference;

  const checkout = cache ? cache.checkout : null;
  const mode = order.toGo
    ? "para retirar"
    : order.toDelivery
      ? "para entrega"
      : `na loja`;
  const inLocoTableText = Number(order.tableId)
    ? `${shop.inLocoTitle} ${order.tableId}`
    : order.tableId;
  const inLocoText =
    checkout && checkout.inLocoLocation
      ? `${checkout.inLocoLocation} ${inLocoTableText}`
      : inLocoTableText;
  const formatter = new Intl.DateTimeFormat("pt-BR", {
    hour: "numeric",
    minute: "numeric",
  });

  const cancelled = status.find(
    (s) => s.code === STATUS_DETAILS.CANCELLED.code,
  );
  const orderedSteps = cancelled
    ? [STATUS_DETAILS.DONE, STATUS_DETAILS.CANCELLED]
    : etapasDoPedido({
        toDelivery: order.toDelivery,
        comPagamento: !!showPaymentStep,
      });

  // A etapa está feita com o evento dela ou com um evento adiante: a loja pode
  // pular o "pronto". Antes a tela esperava o primeiro evento que faltasse, e o
  // título travava ali com o pedido já despachado.
  const orderedStepsRemote = andamentoDoPedido(orderedSteps, status, order);

  const stepWait = orderedStepsRemote.find((o) => !o.feita);

  const showMessageOrderSuccess = shop.messageOrderSuccess && order.isInLoco;
  const cancelModal = () => setModalVisible(false);
  const onCheckout = () => {
    window.mercadoPago.checkout({
      preference: {
        id: paymentPreference.id,
      },
      autoOpen: true,
    });
  };

  const completedCount = orderedStepsRemote.filter((s) => s.feita).length;
  const lastCompleted = [...orderedStepsRemote].reverse().find((s) => s.feita);
  const headline = cancelled
    ? STATUS_DETAILS.CANCELLED.labelCompleted
    : stepWait
      ? stepWait.labelWaiting
      : lastCompleted.labelCompleted;
  const headlineDetail = cancelled
    ? STATUS_DETAILS.CANCELLED.completed
    : stepWait
      ? stepWait.waiting
      : lastCompleted.completed;
  const isPaymentPending =
    stepWait && stepWait.payment && (paymentQRData || paymentPreference);

  return (
    <div className="status-page">
      <Modal
        show={isModalVisible && showMessageOrderSuccess}
        onHide={cancelModal}
      >
        <Modal.Header closeButton>
          <Modal.Title as="h3">Aviso</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <div
            className="w-100 text-center"
            dangerouslySetInnerHTML={{ __html: shop.messageOrderSuccess }}
          />
        </Modal.Body>
      </Modal>

      <CommonHeader text="Acompanhe seu pedido" showBack={!showOnlyStatus} />

      <section
        className={`status-hero${cancelled ? " is-cancelled" : ""}`}
        aria-live="polite"
      >
        {cache && cache.shopName && (
          <p className="status-hero__shop">{cache.shopName}</p>
        )}
        <h1 className="status-hero__title">{headline}</h1>
        {headlineDetail && (
          <p className="status-hero__detail">{headlineDetail}</p>
        )}
        {!cancelled && (
          <ol
            className="status-progress"
            aria-label={`Etapa ${completedCount} de ${orderedStepsRemote.length}`}
          >
            {orderedStepsRemote.map((step) => (
              <li
                key={step.code}
                className={`status-progress__step${
                  step.feita
                    ? " is-done"
                    : stepWait && step.code === stepWait.code
                      ? " is-current"
                      : ""
                }`}
              />
            ))}
          </ol>
        )}
        <p className="status-hero__meta">
          {`Pedido ${mode} · nº ${shortReference}`}
          <br />
          {`Feito em ${formatDateTime(order.createdAt)}`}
          {order.deliveryDateTime && (
            <>
              <br />
              {`Entrega prevista: ${formatDateTime(order.deliveryDateTime)}`}
            </>
          )}
        </p>
        {order.isInLoco && (
          <p className="status-hero__meta">
            {"O pedido será entregue em: "}
            <strong>{inLocoText}</strong>
            {order.isTakingHome && " · embalado para viagem"}
          </p>
        )}
      </section>

      {showMessageOrderSuccess && (
        <div className="notice notice--info">
          <div dangerouslySetInnerHTML={{ __html: shop.messageOrderSuccess }} />
        </div>
      )}

      {isPaymentPending && paymentQRData && (
        <CheckoutSection title="Pague com Pix">
          <PixPayment code={paymentQRData} />
        </CheckoutSection>
      )}

      {isPaymentPending && paymentPreference && (
        <CheckoutSection title="Pagamento">
          <div className="status-card-body">
            <p className="status-card-text">
              O preparo começa depois que o pagamento for aprovado.
            </p>
            <button
              type="button"
              className="btn btn-shop w-100 status-card-button"
              onClick={onCheckout}
            >
              <img src={mpURL} alt="" height="28" className="me-2" />
              Pagar com Mercado Pago
            </button>
          </div>
        </CheckoutSection>
      )}

      <CheckoutSection title="Andamento">
        <ol className="status-timeline">
          {orderedStepsRemote.map((step) => {
            const isCurrent = stepWait && step.code === stepWait.code;
            const state = step.feita
              ? step.cancelled
                ? "is-cancelled"
                : "is-done"
              : isCurrent
                ? "is-current"
                : "is-pending";
            return (
              <li key={step.code} className={`status-timeline__step ${state}`}>
                <span className="status-timeline__marker" aria-hidden="true">
                  {step.feita && !step.cancelled && (
                    <CheckIcon fontSize="inherit" />
                  )}
                  {step.feita && step.cancelled && (
                    <CloseIcon fontSize="inherit" />
                  )}
                </span>
                <div className="status-timeline__body">
                  <p className="status-timeline__title">
                    {stepWait && !step.feita
                      ? step.labelWaiting
                      : step.labelCompleted}
                    {/* etapa feita por um evento adiante fica sem horário */}
                    {step.evento && (
                      <span className="status-timeline__time">
                        {formatter.format(new Date(step.evento.createdAt))}
                      </span>
                    )}
                  </p>
                  <p className="status-timeline__text">
                    {step.feita ? step.completed : step.waiting}
                  </p>
                </div>
              </li>
            );
          })}
        </ol>
      </CheckoutSection>

      <InstallAppCard />

      <OrderDetailsStatus
        shortReference={shortReference}
        cache={cache}
        shop={shop}
        order={order}
        isOrderVisibleInitialValue={true}
      />
    </div>
  );
}

/**
 * Pix no celular: o cliente quase sempre paga no mesmo aparelho, então o
 * "copiar código" vem primeiro e o QR fica para quem paga de outro celular.
 * O status é consultado sozinho, e a tela avança quando o pagamento cai.
 */
function PixPayment({ code }) {
  const [copyState, setCopyState] = React.useState(null);

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(code);
      setCopyState("copied");
    } catch (err) {
      console.log(err);
      setCopyState("error");
    }
  };

  return (
    <div className="pix">
      <button
        type="button"
        className={`btn w-100 status-card-button ${
          copyState === "copied" ? "btn-success" : "btn-shop"
        }`}
        onClick={copy}
      >
        {copyState === "copied" ? (
          <>
            <CheckIcon className="me-2" fontSize="small" />
            Código copiado
          </>
        ) : (
          <>
            <ContentCopyIcon className="me-2" fontSize="small" />
            Copiar código Pix
          </>
        )}
      </button>
      {copyState === "error" && (
        <>
          <p className="pix__hint">
            Não deu para copiar automaticamente. Toque e segure o código abaixo
            para copiar:
          </p>
          <textarea
            className="pix__code"
            readOnly
            value={code}
            rows={4}
            onFocus={(e) => e.target.select()}
          />
        </>
      )}
      <ol className="pix__steps">
        <li>Abra o app do seu banco</li>
        <li>
          Escolha <strong>Pix copia e cola</strong>
        </li>
        <li>Cole o código e confirme o pagamento</li>
      </ol>
      <div className="pix__qr">
        <p className="pix__hint">Ou escaneie de outro aparelho:</p>
        <QRCodeSVG value={code} size={184} />
      </div>
      <p className="pix__note">
        Esta tela atualiza sozinha quando o pagamento for aprovado.
      </p>
    </div>
  );
}

function formatDateTime(value) {
  const date = new Date(value);
  return `${date.toLocaleDateString("pt-BR", {
    dateStyle: "short",
  })} ${date.toLocaleTimeString("pt-BR", { timeStyle: "short" })}`;
}
